var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { FormattedMessage } from 'react-intl';
import RowType from '../../../components/fields/RowType';
import { overlaps } from '../../../utils/DateUtils';
import dayjs from '../../../utils/dayjs';
import { getWeekdaySpan } from '../../../utils/WeekdayUtils';
import { isTimeProper } from './validateDateTime';
/** Ensure normal openings have filled in times/days */
export function validateHoursOfOperationEmpty(rows) {
    var emptyErrors = {
        startDay: {},
        startTime: {},
        endDay: {},
        endTime: {}
    };
    var hasError = false;
    rows.forEach(function (row) {
        if (row.startDay === undefined) {
            emptyErrors.startDay[row.i] = (React.createElement(FormattedMessage, { id: "stripes-core.label.missingRequiredField" }));
            hasError = true;
        }
        if (row.endDay === undefined) {
            emptyErrors.endDay[row.i] = (React.createElement(FormattedMessage, { id: "stripes-core.label.missingRequiredField" }));
            hasError = true;
        }
        if (row.type === RowType.Open) {
            if (row.startTime === undefined) {
                emptyErrors.startTime[row.i] = (React.createElement(FormattedMessage, { id: "stripes-core.label.missingRequiredField" }));
                hasError = true;
            }
            if (row.endTime === undefined) {
                emptyErrors.endTime[row.i] = (React.createElement(FormattedMessage, { id: "stripes-core.label.missingRequiredField" }));
                hasError = true;
            }
        }
    });
    if (hasError) {
        return { empty: emptyErrors };
    }
    return undefined;
}
/** Ensure times are valid */
export function validateHoursOfOperationTimes(rows, timeFieldRefs, localeTimeFormat) {
    var invalidTimeErrors = {
        startTime: {},
        endTime: {}
    };
    rows.forEach(function (row) {
        var _a, _b;
        if (row.type === RowType.Closed) {
            return;
        }
        if (!isTimeProper(localeTimeFormat, row.startTime, (_a = timeFieldRefs.startTime[row.i]) === null || _a === void 0 ? void 0 : _a.value)) {
            invalidTimeErrors.startTime[row.i] = (React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.error.timeFormat", values: { localeTimeFormat: localeTimeFormat } }));
        }
        if (!isTimeProper(localeTimeFormat, row.endTime, (_b = timeFieldRefs.endTime[row.i]) === null || _b === void 0 ? void 0 : _b.value)) {
            invalidTimeErrors.endTime[row.i] = (React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.error.timeFormat", values: { localeTimeFormat: localeTimeFormat } }));
        }
    });
    if (Object.values(invalidTimeErrors.startTime).length ||
        Object.values(invalidTimeErrors.endTime).length) {
        return { invalidTimes: invalidTimeErrors };
    }
    return undefined;
}
/** Split rows into weekday ranges */
export function splitRowsIntoWeekdays(rows) {
    var split = {
        MONDAY: [],
        TUESDAY: [],
        WEDNESDAY: [],
        THURSDAY: [],
        FRIDAY: [],
        SATURDAY: [],
        SUNDAY: []
    };
    var baseDay = dayjs();
    var baseStart = baseDay.startOf('day');
    var baseEnd = baseDay.endOf('day');
    rows.forEach(function (_row) {
        var row = __assign({}, _row);
        if (row.type === RowType.Closed) {
            row.startTime = '00:00';
            row.endTime = '23:59';
        }
        var opening = {
            startDay: row.startDay,
            startTime: row.startTime,
            endDay: row.endDay,
            endTime: row.endTime
        };
        var span = getWeekdaySpan(opening);
        span.forEach(function (weekday, i) {
            var start = baseStart;
            var end = baseEnd;
            var startTime = opening.startTime
                .split(':')
                .map(function (num) { return parseInt(num, 10); });
            var endTime = opening.endTime
                .split(':')
                .map(function (num) { return parseInt(num, 10); });
            if (i === 0) {
                start = start.hour(startTime[0]).minute(startTime[1]);
            }
            if (i === span.length - 1) {
                end = end.hour(endTime[0]).minute(endTime[1]);
            }
            split[weekday].push({
                start: start,
                end: end,
                row: row.i
            });
        });
    });
    return split;
}
/** Check for hours of operation overlaps */
export function validateHoursOfOperationOverlaps(split) {
    var conflicts = new Set();
    Object.values(split).forEach(function (timeRanges) {
        for (var i = 0; i < timeRanges.length - 1; i++) {
            for (var j = i + 1; j < timeRanges.length; j++) {
                if (overlaps(timeRanges[i].start, timeRanges[i].end, timeRanges[j].start, timeRanges[j].end)) {
                    conflicts.add(timeRanges[i].row);
                    conflicts.add(timeRanges[j].row);
                }
            }
        }
    });
    if (conflicts.size) {
        return { conflicts: conflicts };
    }
    return undefined;
}
/** Validate all parts of hours of operation */
export default function validateHoursOfOperation(rows, timeFieldRefs, localeTimeFormat) {
    if (rows === undefined)
        return {};
    var emptyError = validateHoursOfOperationEmpty(rows);
    if (emptyError !== undefined) {
        return { 'hours-of-operation': emptyError };
    }
    var timeError = validateHoursOfOperationTimes(rows, timeFieldRefs, localeTimeFormat);
    if (timeError !== undefined) {
        return { 'hours-of-operation': timeError };
    }
    var split = splitRowsIntoWeekdays(rows);
    return {
        // can be undefined but that is acceptable here
        // as no other cases to check
        'hours-of-operation': validateHoursOfOperationOverlaps(split)
    };
}
